import { useCallback, useEffect, useRef, useState } from 'react';
import { pmGet, pmPost } from '../api';
import './MeetingIntelligence.css';
import { SelectField, PROMPT, UNSET } from '@platform/ui-kit';
import { HelpPanel, type HelpContent } from '../../portfolio_definition/HelpPanel';
import { withFinalizedRule } from '../finalizedRuleHelp';

/**
 * Meeting Intelligence — Transcript Analysis.
 *
 * Cloned from `/Users/glucic/src/ai_readiness_template/views/roadmap-execution/transcript-analysis.ejs`.
 *
 * WHAT IT DOES, in the source's words: upload a meeting transcript and let AI propose
 * project-execution updates — new action items, follow-ups, decisions, questions, risks and
 * work-item progress — all reviewed and approved by you before anything is written.
 *
 * BUDGET AND RESOURCE CONTENT IS NEVER AUTO-APPLIED. Anything the model classifies as
 * `target_type: 'manual'` renders as a Manual Review card with no tick-box, is excluded
 * from the approved count, and is sent with decision 'Manual Review'. That is the screen's
 * central safety rule and it is reproduced exactly.
 *
 * THE DEFAULT APPROVAL RULE is the source's, verbatim: an item arrives approved if the user
 * already decided so, or if it is still Pending, not manual, and its confidence is null or
 * at least 0.4. Below that threshold it arrives unticked — the model's low-confidence
 * guesses do not get applied by someone clicking Apply without reading.
 *
 * TWO VIEWS, ONE ROUTE. The source is an EJS page that reloads with `?result=<id>`; here the
 * result is component state, so Analyze swaps the view in place and "New analysis" swaps it
 * back. Nothing else about either view changes — same cards, same sections, same wording.
 *
 * `alert()` BECOMES AN INLINE LINE, as elsewhere in this module (StreamDefinition,
 * ResourceTracker); `confirm()` before applying is kept, since it guards a write.
 */

interface ProjectRow {
  project_id: string;
  project_description?: string | null;
}

interface OppRow {
  opportunity_id: string;
  description?: string | null;
}

interface UploadRow {
  id: number;
  file_name: string;
  meeting_name?: string | null;
  meeting_date?: string | null;
  created?: string | null;
  opportunity_id?: string | null;
  bytes?: number | null;
  result_id?: number | null;
  result_status?: string | null;
}

interface AnalysisItem {
  id: number | null;
  section: string;
  item_type?: string | null;
  proposed_action?: string | null;
  target_type?: string | null;
  action_type?: string | null;
  related_record_id?: string | number | null;
  title?: string | null;
  description?: string | null;
  owner?: string | null;
  due_date?: string | null;
  priority?: string | null;
  status?: string | null;
  suggested_pct?: number | null;
  phase?: string | null;
  work_item_type?: string | null;
  confidence?: number | null;
  source_excerpt?: string | null;
  user_decision?: string | null;
  /* client-only, as the source's `_approved` / `_edited` / `_dropped` */
  _approved?: boolean;
  _edited?: boolean;
  _dropped?: boolean;
}

interface ResultRow {
  id: number;
  status?: string | null;
  summary?: string | null;
}

/* The source's own constants, verbatim. */
const SECTION_META: Record<string, { label: string; icon: string; color: string }> = {
  new_actions: { label: 'New Action Items', icon: 'add_task', color: '#3949ab' },
  update_actions: { label: 'Existing Action Items to Update', icon: 'edit', color: '#1976d2' },
  close_actions: { label: 'Action Items Recommended for Closure', icon: 'task_alt', color: '#2e7d32' },
  decisions: { label: 'New Decisions', icon: 'gavel', color: '#8e24aa' },
  open_questions: { label: 'Open Questions', icon: 'help', color: '#ef6c00' },
  risks_issues: { label: 'Risks / Issues / Dependencies', icon: 'warning', color: '#c62828' },
  deliverable_progress: { label: 'Deliverable / Activity Progress Updates', icon: 'trending_up', color: '#00897b' },
  manual_review: { label: 'Manual Review Required (not applied)', icon: 'flag', color: '#8a6d1b' },
};
const SECTION_ORDER = [
  'new_actions',
  'update_actions',
  'close_actions',
  'decisions',
  'open_questions',
  'risks_issues',
  'deliverable_progress',
  'manual_review',
];
const PHASES: [string, string][] = [
  ['Assessment_Planning', 'Assessment & Planning'],
  ['Design_Architecture', 'Design & Architecture'],
  ['Development', 'Development'],
  ['Testing_Validation', 'Testing & Validation'],
  ['GoLive_Optimization', 'Go-Live & Optimization'],
];

const d10 = (v?: string | null) => (v ? String(v).slice(0, 10) : '');

/** The source's default approval rule, unchanged. */
function defaultApproved(it: AnalysisItem): boolean {
  const manual = it.target_type === 'manual';
  const approved =
    it.user_decision === 'Approved' ||
    (it.user_decision === 'Pending' &&
      !manual &&
      (it.confidence == null || it.confidence >= 0.4));
  return approved && !manual;
}

/**
 * "How this works" — cloned from `ai_readiness_template/views/roadmap-execution/transcript-analysis.ejs`,
 * its `miHelp` object. Copy, colours and section order are the source's;
 * only the link hrefs are rewritten to this platform's routes, and the entities in
 * fields HelpPanel renders as TEXT (title, section label, term, link label) are
 * decoded, because only the HTML fields go through `dangerouslySetInnerHTML`.
 */
const MI_HELP: HelpContent = {
    title: 'Meeting Intelligence — Transcript Analysis',
    icon: '🎙️',
    purpose: "Upload a <strong>meeting transcript</strong> and let AI propose <strong>project-execution updates</strong> — work-item progress, new action items, follow-ups, decisions, questions and risks — organised <strong>by work item, under the initiative it belongs to</strong>. <strong>Nothing is ever written automatically:</strong> every proposal waits for your approval, and you can edit it or add anything the AI missed. Budget, cost and resource content is never auto-updated (flagged <em>Manual Review</em>).",
    doHere: [
      "Pick a <strong>Project</strong> and at least one <strong>Opportunity</strong> — both are required (marked with a red dot). One meeting often covers several initiatives, so the opportunity picker takes <strong>more than one</strong>.",
      "Add optional meeting details and upload a transcript (.docx / .txt / .vtt / .md), then click <strong>Analyze Transcript</strong>. Analysis runs in the background — the page polls for the result, so a long transcript will not time out.",
      "Review the proposals, <strong>grouped by work item within each initiative</strong>. Adjust the <strong>% complete</strong>, owner or date on any item, tick the ones you want, or use <strong>Approve all</strong> / <strong>Clear all</strong>.",
      "Anything the AI missed you can <strong>add yourself</strong> before applying.",
      "Click <strong>Apply Approved Updates</strong> — only the ticked items are written, with a full audit trail. Or click <strong>Close</strong> to leave without writing anything; the analysis stays in Transcript History and can be reopened."
    ],
    fits: 'Delivery Execution &rarr; <strong>Meeting Intelligence</strong> &rarr; Action Register / Execution Control Center',
    firstTime: "Select a project and its initiative, upload a transcript, click Analyze, review the proposals by work item, then Apply Approved Updates.",
    sections: [
      { label: 'What it will (and won’t) touch', type: 'html', html: "Updates flow to the <strong>Action Register</strong> (new/update/close), <strong>work-item progress</strong>, <strong>risks</strong> and <strong>milestones</strong>. It <strong>never</strong> changes budget, cost, hours, staffing or resource allocation — those are flagged <strong>Manual Review Required</strong> and left for you." },
      {
        label: 'Progress is a percentage — nothing else',
        type: 'html',
        html: "Work-item progress is proposed as a <strong>% complete</strong>, and that percentage is the only thing you set. The state follows from it automatically: <strong>0% = Not Started</strong>, <strong>1&ndash;99% = In Progress</strong>, <strong>100% = Done</strong>. There is no separate status to choose and nothing can be marked <em>Blocked</em> from a transcript &mdash; a concern raised in a meeting becomes a <strong>risk</strong> or an <strong>action</strong>, not a state on the work item."
      },
      {
        label: 'What an action gets when it is created',
        type: 'html',
        html: "An action raised from a transcript arrives ready to follow up on, not just recorded. It carries its <strong>type</strong> (any of the ten in the Action Register &mdash; Action Item, Follow-up, Decision Required, Decision Made, Open Question, Information Request, Dependency, Escalation, Parking Lot, Approval), an owner, a due date, and a <strong>follow-up date set a week before it is due</strong> so it surfaces before it is late. Its <strong>comments</strong> open with a dated line naming the meeting and what was said.<br><br>Updating an existing action moves its progress, status, priority, owner and dates, and <strong>appends</strong> a dated note to its comments. It never changes the action's <strong>type</strong> &mdash; an action keeps the type it was raised as &mdash; and never overwrites your <em>Waiting on</em> classification."
      },
      {
        label: 'Approving, editing and leaving',
        type: 'html',
        html: "Each proposal carries a <strong>tick box</strong>: ticked means it will be written on Apply. A sensible default is suggested, but your choice always wins &mdash; <strong>Approve all</strong> and <strong>Clear all</strong> act on every item at once, and edits you make to a percentage, owner or date are kept.<br><br>Items marked <strong>Manual Review</strong> can never be applied automatically, whatever their tick state.<br><br><strong>Close</strong> leaves the review without writing anything. If items are still ticked it asks first. Nothing is lost &mdash; the analysis is saved in <strong>Transcript History</strong> from the moment it completes, whether or not you apply it."
      }
    ],
    links: [ { label: 'Action Register →', href: '/project_management/program-management/delivery-execution/action-register' }, { label: 'Execution Control Center →', href: '/project_management/program-management/delivery-execution/execution-control-center' } ]
  };

export function MeetingIntelligence() {
  const [projects, setProjects] = useState<ProjectRow[]>([]);
  const [opps, setOpps] = useState<OppRow[]>([]);
  const [projectId, setProjectId] = useState('');
  const [oppId, setOppId] = useState(UNSET);
  const [meeting, setMeeting] = useState({
    meeting_name: '',
    meeting_type: '',
    meeting_date: '',
    client_internal: '',
    workstream: '',
    phase: '',
    related_milestone: '',
  });
  const [uploads, setUploads] = useState<UploadRow[] | null>(null);
  const [histMsg, setHistMsg] = useState('Select a project to see its uploaded transcripts.');
  const [status, setStatus] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  /* The review view. Null means the intake view — the source's `if (!result)`. */
  const [result, setResult] = useState<ResultRow | null>(null);
  const [items, setItems] = useState<AnalysisItem[]>([]);
  /* Injected by the server, as the source's route injects them into the page. */
  const [statuses, setStatuses] = useState<string[]>([]);
  const [priorities, setPriorities] = useState<string[]>([]);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const d = await pmGet<{ projects: ProjectRow[] }>('/meeting-intelligence/projects');
        setProjects(d.projects ?? []);
      } catch {
        /* the picker simply stays empty */
      }
    })();
  }, []);

  const loadHistory = useCallback(async (pid: string, oppRaw: string) => {
    if (!pid) return;
    /* UNSET and '' both mean "All / project-level" — the query is unchanged. */
    const opp = oppRaw === UNSET ? '' : oppRaw;
    setHistMsg('Loading…');
    try {
      const q = `project_id=${encodeURIComponent(pid)}${opp ? `&opportunity_id=${encodeURIComponent(opp)}` : ''}`;
      const d = await pmGet<{ uploads: UploadRow[] }>(`/meeting-intelligence/uploads?${q}`);
      const rows = d.uploads ?? [];
      setUploads(rows);
      if (!rows.length) setHistMsg('No transcripts uploaded for this scope yet.');
    } catch {
      setUploads(null);
      setHistMsg('Failed to load history.');
    }
  }, []);

  const onProject = async (pid: string) => {
    setProjectId(pid);
    setOppId('');
    setOpps([]);
    setUploads(null);
    if (!pid) {
      setHistMsg('Select a project to see its uploaded transcripts.');
      return;
    }
    try {
      const d = await pmGet<{ opportunities: OppRow[] }>(
        `/meeting-intelligence/opportunities?project_id=${encodeURIComponent(pid)}`,
      );
      setOpps(d.opportunities ?? []);
    } catch {
      /* the source swallows this too */
    }
    void loadHistory(pid, '');
  };

  const openResult = useCallback(async (resultId: number) => {
    try {
      const d = await pmGet<{
        success: boolean;
        error?: string;
        result: ResultRow;
        items: AnalysisItem[];
        STATUSES?: string[];
        PRIORITIES?: string[];
      }>(`/meeting-intelligence/result?result_id=${encodeURIComponent(String(resultId))}`);
      if (!d.success) {
        setNotice(`Error: ${d.error || 'failed'}`);
        return;
      }
      setStatuses(d.STATUSES ?? []);
      setPriorities(d.PRIORITIES ?? []);
      setResult(d.result);
      /*
       * §6.3 bug 2 — THE DEFAULT IS APPLIED ONLY WHERE THE VALUE HAS NEVER BEEN SET.
       *
       * Recomputing it on every load re-ticks everything the user just cleared, which is what
       * produced "Clear all does not clear all check boxes": the clear worked, and the next
       * render put every tick back. Existing decisions are carried across by id.
       */
      setItems((prev) => {
        const seen = new Map(prev.map((x) => [x.id, x._approved]));
        return (d.items ?? []).map((it) => ({
          ...it,
          due_date: d10(it.due_date),
          _approved: seen.has(it.id) ? seen.get(it.id) : defaultApproved(it),
        }));
      });
    } catch {
      setNotice('Request failed');
    }
  }, []);

  /**
   * Wait for a background analysis to finish — §6.4.
   *
   * `/analyze` now returns as soon as the result row exists, because App Runner kills any
   * request at 120 seconds and a real transcript takes one to three minutes. The work carries
   * on server-side and this polls for it.
   *
   * There is no client-side deadline: the SERVER decides when a run is stale (20 minutes) and
   * reports it failed. The browser cannot tell a slow analysis from a dead one, and a deadline
   * here would abandon one that was still working.
   */
  const awaitAnalysis = useCallback(async (resultId: number): Promise<boolean> => {
    for (;;) {
      await new Promise((r) => setTimeout(r, 5000));
      const st = await pmGet<{ success: boolean; status: string; error?: string | null }>(
        `/meeting-intelligence/analysis-status?result_id=${resultId}`,
      );
      if (st.status === 'analyzing') continue;
      if (st.status === 'failed') {
        setNotice(st.error || 'The analysis failed.');
        return false;
      }
      return true;
    }
  }, []);

  /**
   * A multipart POST, so it does NOT go through the JSON client — `fetch` with a FormData
   * body and no Content-Type header, letting the browser set the multipart boundary. The
   * same approach `ReportTemplates` takes for its upload.
   */
  const uploadAndAnalyze = async () => {
    const file = fileRef.current?.files?.[0];
    if (!projectId) {
      setNotice('Select a project.');
      return;
    }
    if (!file) {
      setNotice('Choose a transcript file.');
      return;
    }
    setBusy(true);
    setNotice(null);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('project_id', projectId);
    if (oppId && oppId !== UNSET) fd.append('opportunity_id', oppId);
    (Object.keys(meeting) as (keyof typeof meeting)[]).forEach((k) => {
      if (meeting[k]) fd.append(k, meeting[k]);
    });
    try {
      setStatus('Uploading transcript…');
      const upRes = await fetch('/api/project_management/meeting-intelligence/upload', {
        method: 'POST',
        credentials: 'same-origin',
        body: fd,
      });
      const up = (await upRes.json()) as { success: boolean; error?: string; upload_id?: number };
      if (!up.success) {
        setNotice(`Upload failed: ${up.error || ''}`);
        setBusy(false);
        setStatus('');
        return;
      }
      setStatus('Analyzing with AI… (this can take up to a minute)');
      const an = await pmPost<{ success: boolean; error?: string; result_id?: number }>(
        '/meeting-intelligence/analyze',
        { upload_id: up.upload_id },
      );
      if (!an.success || !an.result_id) {
        setNotice(`Analysis failed: ${an.error || ''}`);
        setBusy(false);
        setStatus('Uploaded, but analysis failed — retry from history.');
        void loadHistory(projectId, oppId);
        return;
      }
      setStatus('Analyzing with AI… you can leave this page open.');
      const ok = await awaitAnalysis(an.result_id);
      setStatus('');
      setBusy(false);
      if (ok) await openResult(an.result_id);
      else void loadHistory(projectId, oppId);
    } catch (e) {
      setNotice(`Request failed: ${(e as Error).message}`);
      setBusy(false);
      setStatus('');
    }
  };

  const analyzeExisting = async (uploadId: number) => {
    setBusy(true);
    try {
      const an = await pmPost<{ success: boolean; error?: string; result_id?: number }>(
        '/meeting-intelligence/analyze',
        { upload_id: uploadId },
      );
      if (!an.success || !an.result_id) {
        setNotice(`Analysis failed: ${an.error || ''}`);
        setBusy(false);
        return;
      }
      const ok2 = await awaitAnalysis(an.result_id);
      setBusy(false);
      if (ok2) await openResult(an.result_id);
    } catch {
      setNotice('Request failed');
      setBusy(false);
    }
  };

  const delUpload = async (id: number) => {
    if (!window.confirm('Delete this transcript and its analysis?')) return;
    try {
      await pmPost(`/meeting-intelligence/upload/${id}/delete`);
      void loadHistory(projectId, oppId);
    } catch {
      /* the source swallows this */
    }
  };

  const edit = (idx: number, field: keyof AnalysisItem, val: string) =>
    setItems((cur) =>
      cur.map((it, i) =>
        i === idx
          ? {
              ...it,
              [field]:
                field === 'suggested_pct' ? (val === '' ? null : parseInt(val, 10)) : val,
              ...(it._approved ? { _edited: true } : {}),
            }
          : it,
      ),
    );

  const toggleItem = (idx: number, on: boolean) =>
    setItems((cur) => cur.map((it, i) => (i === idx ? { ...it, _approved: on } : it)));

  const dropItem = (idx: number) =>
    setItems((cur) => cur.map((it, i) => (i === idx ? { ...it, _dropped: true } : it)));

  const setAll = (on: boolean) =>
    setItems((cur) =>
      cur.map((it) =>
        it.target_type !== 'manual' && !it._dropped ? { ...it, _approved: on } : it,
      ),
    );

  const addItem = () =>
    setItems((cur) => [
      ...cur,
      {
        id: null,
        section: 'new_actions',
        item_type: 'Action Item',
        proposed_action: 'new_action',
        target_type: 'action',
        action_type: 'Action Item',
        related_record_id: null,
        title: '',
        owner: '',
        due_date: '',
        priority: 'Medium',
        status: 'Not Started',
        suggested_pct: null,
        confidence: null,
        source_excerpt: null,
        user_decision: 'Approved',
        _approved: true,
      },
    ]);

  const proposedCount = items.filter((it) => it.target_type !== 'manual').length;
  const approvedCount = items.filter(
    (it) => it._approved && !it._dropped && it.target_type !== 'manual',
  ).length;

  const applyUpdates = async () => {
    if (!result) return;
    const payload = {
      result_id: result.id,
      items: items
        .filter((it) => !it._dropped)
        .map((it) => ({
          id: it.id || null,
          section: it.section,
          proposed_action: it.proposed_action,
          target_type: it.target_type,
          related_record_id: it.related_record_id,
          action_type: it.action_type,
          title: it.title,
          description: it.description,
          owner: it.owner,
          due_date: it.due_date || null,
          priority: it.priority,
          status: it.status,
          suggested_pct: it.suggested_pct,
          phase: it.phase,
          work_item_type: it.work_item_type,
          source_excerpt: it.source_excerpt,
          decision:
            it.target_type === 'manual'
              ? 'Manual Review'
              : it._approved
                ? it._edited
                  ? 'Edited'
                  : 'Approved'
                : 'Rejected',
        })),
    };
    const approved = payload.items.filter(
      (i) => i.decision === 'Approved' || i.decision === 'Edited',
    ).length;
    if (!approved) {
      setNotice('Approve at least one item to apply.');
      return;
    }
    if (
      !window.confirm(
        `Apply ${approved} approved update${approved === 1 ? '' : 's'} to the project records?`,
      )
    )
      return;
    setApplying(true);
    try {
      const d = await pmPost<{
        success: boolean;
        error?: string;
        applied?: Record<string, number>;
      }>('/meeting-intelligence/apply', payload);
      if (!d.success) {
        setNotice(`Apply failed: ${d.error || ''}`);
        setApplying(false);
        return;
      }
      const a = d.applied ?? {};
      setNotice(
        `Applied ✓  —  ${a.created_actions ?? 0} new actions, ${a.updated_actions ?? 0} updated, ` +
          `${a.closed_actions ?? 0} closed, ${a.new_deliverables ?? 0} new work items, ` +
          `${a.updated_deliverables ?? 0} work-item updates, ${a.risks ?? 0} risks, ` +
          `${a.events ?? 0} milestones.`,
      );
      setApplying(false);
      await openResult(result.id);
    } catch {
      setNotice('Request failed');
      setApplying(false);
    }
  };

  const fld = (k: keyof typeof meeting) => ({
    value: meeting[k],
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setMeeting((m) => ({ ...m, [k]: e.target.value })),
  });

  /* ── The review view ──────────────────────────────────────────────────── */
  if (result) {
    const bySection = new Map<string, number[]>();
    items.forEach((it, i) => {
      if (it._dropped) return;
      const list = bySection.get(it.section) ?? [];
      list.push(i);
      bySection.set(it.section, list);
    });

    return (
      <div className="pm-mi">
        <div className="mi-wrap">
          <h1 className="page-header__title">Meeting Intelligence</h1>
          <p className="mi-lede">
            Transcript Analysis — upload a meeting transcript and review AI-proposed execution
            updates before anything is written. Budget &amp; resource content is never
            auto-updated.
          </p>

          <button
            type="button"
            className="mi-back"
            onClick={() => {
              setResult(null);
              setItems([]);
              setNotice(null);
            }}
          >
            <i className="material-icons" style={{ fontSize: 16 }}>
              arrow_back
            </i>{' '}
            New analysis
          </button>

          <div className="mi-bar">
            <div>
              <div style={{ fontWeight: 700 }}>Proposed updates</div>
              <div className="cnt">
                {approvedCount} approved of {proposedCount} proposed
              </div>
            </div>
            <div className="grow" />
            <button type="button" className="mi-btn ghost" onClick={() => setAll(true)}>
              Approve all
            </button>
            <button type="button" className="mi-btn ghost" onClick={() => setAll(false)}>
              Clear all
            </button>
            {/*
              CLOSE — leaves without writing anything. §6.6.
              The "New analysis" back-link must not be the ONLY exit; the owner rejected that
              explicitly. If items are still ticked we confirm, because leaving then looks like
              it might have applied them. The analysis stays in history either way, so nothing
              is lost by closing.
            */}
            <button
              type="button"
              className="mi-btn ghost"
              onClick={() => {
                if (
                  approvedCount > 0 &&
                  !window.confirm(
                    `${approvedCount} update(s) are still approved but NOT applied.\n\n` +
                      'Close without applying them? The analysis stays in history and you can ' +
                      'reopen it.',
                  )
                ) {
                  return;
                }
                setResult(null);
                setItems([]);
                setNotice(null);
              }}
            >
              Close
            </button>
            {result.status === 'applied' ? (
              <span style={{ fontWeight: 700, color: '#a5d6a7' }}>✓ Applied</span>
            ) : (
              <button
                type="button"
                className="mi-btn"
                style={{ background: '#2e7d32' }}
                onClick={() => void applyUpdates()}
                disabled={applying}
              >
                <i className="material-icons" style={{ fontSize: 18 }}>
                  task_alt
                </i>{' '}
                {applying ? 'Applying…' : 'Apply Approved Updates'}
              </button>
            )}
          </div>

          {result.summary ? (
            <div className="mi-summary">
              <strong>Meeting summary:</strong> {result.summary}
            </div>
          ) : null}

          {notice ? <div className="mi-summary">{notice}</div> : null}

          {/* Only the item list scrolls — everything above stays put. §6.6. */}
          <div className="ta-body">
            {items.length === 0 ? (
              <div className="mi-empty">No actionable updates were found in this transcript.</div>
            ) : null}
            {SECTION_ORDER.map((sec) => {
              const idxs = bySection.get(sec);
              if (!idxs || !idxs.length) return null;
              const meta = SECTION_META[sec]!;
              return (
                <div className="mi-sec" key={sec}>
                  <div className="mi-sec-head" style={{ background: meta.color }}>
                    <i className="material-icons" style={{ fontSize: 18 }}>
                      {meta.icon}
                    </i>{' '}
                    {meta.label} <span style={{ opacity: 0.8 }}>({idxs.length})</span>
                  </div>
                  <div className="mi-sec-body">
                    {idxs.map((i) => {
                      const it = items[i]!;
                      const manual = it.target_type === 'manual';
                      if (manual) {
                        return (
                          <div className="mi-manual" key={i}>
                            <strong>⚑ {it.item_type || 'Manual review'}:</strong> {it.title || ''}
                            {it.description ? ` — ${it.description}` : ''}
                            {it.source_excerpt ? (
                              <div className="mi-exc">“{it.source_excerpt}”</div>
                            ) : null}
                            <div style={{ fontSize: 11, marginTop: 4 }}>
                              Not applied — budget/resource content is manual-only.
                            </div>
                          </div>
                        );
                      }
                      const showPct =
                        it.proposed_action === 'update_deliverable' ||
                        it.proposed_action === 'close_action' ||
                        it.proposed_action === 'update_action' ||
                        it.proposed_action === 'new_deliverable';
                      const isNewDeliv = it.proposed_action === 'new_deliverable';
                      return (
                        <div className={`mi-item${it._approved ? '' : ' off'}`} key={i}>
                          <div className="mi-item-top">
                            <input
                              type="checkbox"
                              className="mi-chk"
                              checked={!!it._approved}
                              onChange={(e) => toggleItem(i, e.target.checked)}
                            />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 8,
                                  flexWrap: 'wrap',
                                }}
                              >
                                <span className="chip" style={{ background: '#eef2ff', color: '#3949ab' }}>
                                  {it.item_type || ''}
                                </span>
                                {isNewDeliv ? (
                                  <span className="chip" style={{ background: '#e0f2f1', color: '#00695c' }}>
                                    ＋ New work item ({it.work_item_type || 'Deliverable'})
                                  </span>
                                ) : null}
                                {it.related_record_id ? (
                                  <span className="mi-rel">
                                    ↳ {it.target_type === 'deliverable' ? 'work item #' : 'ACT-'}
                                    {it.related_record_id}
                                  </span>
                                ) : null}
                                {it.confidence != null ? (
                                  <span className="mi-conf">
                                    {Math.round(it.confidence * 100)}% conf
                                  </span>
                                ) : null}
                                <button
                                  type="button"
                                  className="mi-ico-btn"
                                  title="Remove"
                                  style={{ marginLeft: 'auto' }}
                                  onClick={() => dropItem(i)}
                                >
                                  ✕
                                </button>
                              </div>

                              <div className="egrid">
                                <div className="mi-fld wide">
                                  <label>Title</label>
                                  <input
                                    type="text"
                                    value={it.title || ''}
                                    onChange={(e) => edit(i, 'title', e.target.value)}
                                  />
                                </div>
                                <div className="mi-fld">
                                  <label>Owner</label>
                                  <input
                                    type="text"
                                    value={it.owner || ''}
                                    onChange={(e) => edit(i, 'owner', e.target.value)}
                                  />
                                </div>
                                <div className="mi-fld">
                                  <label>Due</label>
                                  <input
                                    type="date"
                                    value={d10(it.due_date)}
                                    onChange={(e) => edit(i, 'due_date', e.target.value)}
                                  />
                                </div>
                                <div className="mi-fld">
                                  <label>Priority</label>
                                  <select
                                    value={it.priority || 'Medium'}
                                    onChange={(e) => edit(i, 'priority', e.target.value)}
                                  >
                                    {priorities.map((s) => (
                                      <option key={s}>{s}</option>
                                    ))}
                                  </select>
                                </div>
                                <div className="mi-fld">
                                  <label>Status</label>
                                  <select
                                    value={it.status || 'Not Started'}
                                    onChange={(e) => edit(i, 'status', e.target.value)}
                                  >
                                    {statuses.map((s) => (
                                      <option key={s}>{s}</option>
                                    ))}
                                  </select>
                                </div>
                                {isNewDeliv ? (
                                  <div className="mi-fld">
                                    <label>Phase</label>
                                    <select
                                      value={it.phase || PHASES[0]![0]}
                                      onChange={(e) => edit(i, 'phase', e.target.value)}
                                    >
                                      {PHASES.map(([v, l]) => (
                                        <option key={v} value={v}>
                                          {l}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                ) : null}
                                {showPct ? (
                                  <div className="mi-fld">
                                    <label>% Complete</label>
                                    <input
                                      type="number"
                                      min={0}
                                      max={100}
                                      value={it.suggested_pct != null ? it.suggested_pct : ''}
                                      onChange={(e) => edit(i, 'suggested_pct', e.target.value)}
                                    />
                                  </div>
                                ) : null}
                              </div>

                              {it.source_excerpt ? (
                                <div className="mi-exc">“{it.source_excerpt}”</div>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{ marginTop: 10 }}>
            <button type="button" className="mi-btn ghost" onClick={addItem}>
              <i className="material-icons" style={{ fontSize: 17 }}>
                add
              </i>{' '}
              Add an action item
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ── The intake view ──────────────────────────────────────────────────── */
  return (
    <div className="pm-mi">
      <HelpPanel help={withFinalizedRule(MI_HELP)} />
      <div className="mi-wrap">
        <h1 className="page-header__title">Meeting Intelligence</h1>
        <p className="mi-lede">
          Transcript Analysis — upload a meeting transcript and review AI-proposed execution
          updates before anything is written. Budget &amp; resource content is never
          auto-updated.
        </p>

        <div className="ta-cols">
          <div className="ta-left">
            <div className="mi-card">
              <h3>
                <i className="material-icons" style={{ color: '#3949ab' }}>
                  upload_file
                </i>{' '}
                Analyze a transcript
              </h3>
              <div className="mi-grid">
                <div className="mi-fld">
                  <SelectField
                    label="Project"
                    orientation="stacked"
                    width="lg"
                    placeholder={PROMPT.project}
                    value={projectId}
                    onChange={(v) => void onProject(v)}
                    options={projects.map((p) => ({
                      value: p.project_id,
                      id: p.project_id,
                      description: p.project_description,
                    }))}
                  />
                </div>
                <div className="mi-fld">
                  <SelectField
                    label="Opportunity"
                    orientation="stacked"
                    width="lg"
                    placeholder={PROMPT.opportunity}
                    help="Optional — leave on All for project-level."
                    value={oppId}
                    disabled={!projectId}
                    onChange={(v) => {
                      setOppId(v);
                      void loadHistory(projectId, v);
                    }}
                    options={[
                      // Blank is a real choice here, not an empty prompt — so it stays an
                      // option and SelectField adds no clear row of its own.
                      { value: '', label: 'All / project-level' },
                      ...opps.map((o) => ({
                        value: o.opportunity_id,
                        id: o.opportunity_id,
                description: o.description,
                      })),
                    ]}
                  />
                </div>
                <div className="mi-fld">
                  <label>Meeting name</label>
                  <input type="text" placeholder="e.g. Weekly status — Jul 9" {...fld('meeting_name')} />
                </div>
                <div className="mi-fld">
                  <label>Meeting type</label>
                  <input type="text" placeholder="e.g. Status / SteerCo / Workshop" {...fld('meeting_type')} />
                </div>
                <div className="mi-fld">
                  <label>Meeting date</label>
                  <input type="date" {...fld('meeting_date')} />
                </div>
                <div className="mi-fld">
                  <label>Client / Internal</label>
                  <select {...fld('client_internal')}>
                    <option value="">—</option>
                    <option>Client</option>
                    <option>Internal</option>
                  </select>
                </div>
                <div className="mi-fld">
                  <label>Workstream</label>
                  <input type="text" placeholder="optional" {...fld('workstream')} />
                </div>
                <div className="mi-fld">
                  <label>Phase</label>
                  <input type="text" placeholder="optional" {...fld('phase')} />
                </div>
                <div className="mi-fld full">
                  <label>Related milestone</label>
                  <input type="text" placeholder="optional" {...fld('related_milestone')} />
                </div>
                <div className="mi-fld full">
                  <div className="mi-drop">
                    <i className="material-icons" style={{ fontSize: 30, color: '#90a4ae' }}>
                      description
                    </i>
                    <div>
                      Drop or choose a transcript file — <strong>.docx, .txt, .vtt, .md</strong>
                    </div>
                    <input ref={fileRef} type="file" accept=".docx,.txt,.vtt,.md,.csv" />
                  </div>
                </div>
              </div>
              <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 12 }}>
                <button
                  type="button"
                  className="mi-btn"
                  onClick={() => void uploadAndAnalyze()}
                  disabled={busy}
                >
                  <i className="material-icons" style={{ fontSize: 18 }}>
                    auto_awesome
                  </i>{' '}
                  Analyze Transcript
                </button>
                <span style={{ fontSize: 12.5, color: '#78909c' }}>{status}</span>
              </div>
              {notice ? (
                <div style={{ marginTop: 10, fontSize: 12.5, color: '#c62828', fontWeight: 600 }}>
                  {notice}
                </div>
              ) : null}
            </div>
          </div>

          <div className="ta-right">
            <div className="mi-card">
              <h3>
                <i className="material-icons" style={{ color: '#607d8b' }}>
                  history
                </i>{' '}
                Transcript history
              </h3>
              {uploads && uploads.length ? (
                <table className="mi-hist">
                  <thead>
                    <tr>
                      <th>Meeting / File</th>
                      <th>Scope</th>
                      <th>Date</th>
                      <th>Analysis</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {uploads.map((u) => (
                      <tr key={u.id}>
                        <td>
                          <strong>{u.meeting_name || u.file_name}</strong>
                          <div style={{ fontSize: 11, color: '#90a4ae' }}>
                            {u.file_name}
                            {u.bytes ? ` · ${Math.round(u.bytes / 1024)} KB` : ''}
                          </div>
                        </td>
                        <td>{u.opportunity_id || 'Project-level'}</td>
                        <td>{u.meeting_date || u.created || ''}</td>
                        <td>
                          {u.result_id ? (
                            <button
                              type="button"
                              className="mi-back"
                              style={{ marginBottom: 0 }}
                              onClick={() => void openResult(u.result_id!)}
                            >
                              Open ({u.result_status || ''})
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="mi-btn ghost"
                              style={{ padding: '4px 10px', fontSize: 12 }}
                              disabled={busy}
                              onClick={() => void analyzeExisting(u.id)}
                            >
                              Analyze
                            </button>
                          )}
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          <a
                            className="mi-ico-btn"
                            href={`/api/project_management/meeting-intelligence/transcript/${u.id}/download`}
                            title="Download"
                          >
                            ⬇️
                          </a>
                          <button
                            type="button"
                            className="mi-ico-btn"
                            title="Delete"
                            onClick={() => void delUpload(u.id)}
                          >
                            🗑️
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="mi-empty">{histMsg}</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
